<template>
  <div class="rmr-keyword-chips" :class="`rmr-keyword-chips--${variant}`">
    <span v-if="label" class="rmr-keyword-chips__label">{{ label }}</span>

    <div class="rmr-keyword-chips__list">
      <v-chip
        v-for="keyword in visibleKeywords"
        :key="keyword"
        :small="variant === 'ghost'"
        :x-small="variant === 'default'"
        class="rmr-keyword-chips__chip"
      >
        {{ keyword }}
      </v-chip>

      <v-tooltip v-if="hiddenKeywords.length" bottom :content-class="tooltipClass">
        <template v-slot:activator="{ on, attrs }">
          <v-chip
            :small="variant === 'ghost'"
            :x-small="variant === 'default'"
            class="rmr-keyword-chips__chip rmr-keyword-chips__chip--more"
            v-bind="attrs"
            v-on="on"
          >
            +{{ hiddenKeywords.length }}
          </v-chip>
        </template>

        <div class="rmr-keyword-chips__tooltip">
          <v-chip
            v-for="keyword in hiddenKeywords"
            :key="'hidden-' + keyword"
            :small="variant === 'ghost'"
            :x-small="variant === 'default'"
            class="rmr-keyword-chips__chip"
          >
            {{ keyword }}
          </v-chip>
        </div>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
export default {
  name: "RmrKeywordChips",
  props: {
    keywords: {
      type: Array,
      required: true,
    },
    maxVisible: {
      type: Number,
      default: 3,
    },
    variant: {
      type: String,
      default: "default",
      validator: (value) => ["default", "ghost"].includes(value),
    },
    label: {
      type: String,
      default: "",
    },
  },
  computed: {
    visibleKeywords() {
      return this.keywords.slice(0, this.maxVisible);
    },
    hiddenKeywords() {
      return this.keywords.slice(this.maxVisible);
    },
    tooltipClass() {
      return this.variant === "default"
        ? "rmr-kc-tooltip rmr-kc-tooltip--light"
        : "rmr-kc-tooltip rmr-kc-tooltip--dark";
    },
  },
};
</script>

<style lang="scss">
.rmr-keyword-chips {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  width: 100%;

  &__label {
    flex: 0 0 auto;
  }

  &__list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    min-width: 0;
    flex: 1 1 auto;
  }

  &__chip {
    font-weight: 700 !important;
    max-width: 100%;

    .v-chip__content {
      display: block;
    }
  }

  &__tooltip {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-width: 320px;
    padding: 4px 2px;
  }
}

// default variant — filled indigo chips (light surfaces)
.rmr-keyword-chips--default .rmr-keyword-chips__chip {
  background: $rmr-indigo-bg !important;
  color: $rmr-indigo-accent !important;
  border-radius: $rmr-radius-sm !important;
  height: 24px !important;
  padding: 0 8px !important;
}

.rmr-keyword-chips--default .rmr-keyword-chips__chip--more {
  background: $rmr-border-hover !important;
  color: $rmr-text-medium !important;
}

// ghost variant — white semi-transparent chips (dark/gradient surfaces)
.rmr-keyword-chips--ghost .rmr-keyword-chips__chip {
  background: rgba(255, 255, 255, 0.12) !important;
  border: 1px solid rgba(255, 255, 255, 0.18) !important;
  color: $rmr-surface-0 !important;
}

.rmr-keyword-chips--ghost .rmr-keyword-chips__chip--more {
  background: rgba(15, 23, 42, 0.24) !important;
}

.rmr-keyword-chips--ghost .rmr-keyword-chips__label {
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: rgba(255, 255, 255, 0.62);
}

// Tooltip wrapper — light (default variant)
.rmr-kc-tooltip--light.v-tooltip__content {
  background: $rmr-surface-0 !important;
  border: 1px solid $rmr-border-hover !important;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.12) !important;
  border-radius: $rmr-radius-lg !important;
  padding: 10px !important;
  opacity: 1 !important;
}

// Chips inside light tooltip — same indigo style as visible chips
.rmr-kc-tooltip--light .rmr-keyword-chips__chip {
  background: $rmr-indigo-bg !important;
  color: $rmr-indigo-accent !important;
  border-radius: $rmr-radius-sm !important;
  height: 24px !important;
  padding: 0 8px !important;
  font-weight: 700 !important;
}

// Tooltip wrapper — dark (ghost variant)
.rmr-kc-tooltip--dark.v-tooltip__content {
  opacity: 1 !important;
  border-radius: $rmr-radius-md !important;
  padding: 10px !important;
}

// Chips inside dark tooltip — same ghost style as visible chips
.rmr-kc-tooltip--dark .rmr-keyword-chips__chip {
  background: rgba(255, 255, 255, 0.12) !important;
  border: 1px solid rgba(255, 255, 255, 0.18) !important;
  color: $rmr-surface-0 !important;
  font-weight: 700 !important;
}
</style>
